import React, { useState, useRef } from 'react'
import { parse } from 'papaparse'
import moment from 'moment'
import { addBulkPromoItems } from '../../../lib/actions'

const PromoCSVUploadModal = ({ isOpen, onClose, onComplete, existingPromos }) => {
    const [file, setFile] = useState(null)
    const [validationResults, setValidationResults] = useState([])
    const [summary, setSummary] = useState(null)
    const [isUploading, setIsUploading] = useState(false)
    const [validationMessage, setValidationMessage] = useState({ type: null, message: null })
    const fileInputRef = useRef(null)

    const EXPECTED_CSV_FORMAT = ['code', 'expiration_date']

    const resetForm = (clearFileInput = true) => {
        setValidationResults([])
        setSummary(null)
        setValidationMessage(null)
        if (clearFileInput && fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const validateRow = (rowData, rowIndex) => {
        const errors = []
        const extracted = {}

        if (!rowData.code || typeof rowData.code !== 'string') {
            errors.push('Invalid code')
        }
        extracted.code = rowData.code?.trim().toUpperCase()

        const exp = moment(rowData.expiration_date, 'YYYY-MM-DD', true)
        if (!exp.isValid()) {
            errors.push('Invalid expiration date (YYYY-MM-DD)')
        } else if (exp.isBefore(moment(), 'day')) {
            errors.push('Expiration date in the past')
        }
        extracted.expiration_date = rowData.expiration_date

        return {
            row: rowIndex + 1,
            data: extracted,
            errors
        }
    }

    const validateCsv = (data, existingCodesSet) => {
        if (!data?.length) {
            setValidationMessage({ type: 'error', message: 'CSV file is empty' })
            return { isValid: false, criticalError: true }
        }

        const csvHeaders = Object.keys(data[0]).map(h => h.toLowerCase().replace(/\s+/g, '_'))
        const missing = EXPECTED_CSV_FORMAT.filter(h => !csvHeaders.includes(h))
        if (missing.length) {
            setValidationMessage({ type: 'error', message: `Missing columns: ${missing.join(', ')}` })
            return { isValid: false, criticalError: true }
        }

        const seenCsv = new Set()
        const results = data.map((row, idx) => {
            const record = validateRow(row, idx)
            const code = record.data.code

            if (!record.errors.includes('Invalid code')) {
                if (seenCsv.has(code)) {
                    record.errors.push('Duplicate code in this CSV')
                } else {
                    seenCsv.add(code)
                }

                if (existingCodesSet.has(code)) {
                    record.errors.push('Code already exists in database')
                }
            }

            return record
        })

        const hasErrors = results.some(r => r.errors.length)
        return {
            isValid: !hasErrors,
            results,
            error: hasErrors ? 'Some rows have issues' : null
        }
    }

    const handleFileChange = (e) => {
        const selected = e.target.files[0]
        if (!selected) return

        resetForm(false)
        if (!selected.name.toLowerCase().endsWith('.csv')) {
            setValidationMessage({ type: 'error', message: 'Upload a .csv file' })
            return
        }

        setFile(selected)
    }

    const handleValidateCSV = async () => {
        if (!file) {
            setValidationMessage({ type: 'error', message: 'Please select a CSV file first' })
            return
        }

        setIsUploading(true)
        setValidationMessage(null)

        parse(file, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (header) => header.trim().toLowerCase().replace(/\s+/g, '_'),
            complete: (results) => {
                if (!results.data || results.data.length === 0) {
                    setValidationMessage({ type: 'error', message: 'CSV file is empty' })
                    setIsUploading(false)
                    return
                }

                const existingCodesSet = new Set(
                    (existingPromos || []).map(p => p.code?.toUpperCase())
                )

                const validation = validateCsv(results.data, existingCodesSet)

                if (validation?.isValid || !validation?.criticalError) {
                    const validRecords = validation?.results.filter(r => r.errors.length === 0)
                    const invalidRecords = validation?.results.filter(r => r.errors.length > 0)

                    setValidationResults(validation?.results)
                    setSummary({
                        total: validation?.results.length,
                        valid: validRecords?.length,
                        invalid: invalidRecords?.length,
                    })
                } else {
                    setValidationResults([])
                    setSummary(null)
                }

                setIsUploading(false)
            },
            error: (error) => {
                setValidationMessage({ type: 'error', message: `Error parsing CSV: ${error.message}` })
                setIsUploading(false)
            }
        })
    }

    const handleUploadPromos = async () => {
        if (!summary?.valid) return
        setIsUploading(true)
        try {
            const promos = validationResults
                .filter(r => r.errors.length === 0)
                .map(r => ({
                    code: r.data.code,
                    expirationDate: r.data.expiration_date,
                    isActive: true,
                    redeemLimit: 1,
                    redeemed: 0,
                    createdAt: new Date().toISOString()
                }))

            const successCount = await addBulkPromoItems(promos)
            setValidationMessage({ type: 'success', message: `Uploaded ${successCount} promo codes` })

            setTimeout(() => {
                onComplete(true)
                onClose()
                resetForm()
            }, 1500)
        } catch (err) {
            setValidationMessage({ type: 'error', message: `Upload failed: ${err.message}` })
        } finally {
            setIsUploading(false)
        }
    }

    const showSummary = summary && !(validationMessage?.type === 'error')

    return (
        <div className={`modal ${isOpen ? 'show d-block' : ''}`} tabIndex="-1">
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Upload Promo Codes CSV</h5>
                        <button
                            type="button"
                            className="btn-close"
                            disabled={isUploading}
                            onClick={() => {
                                onClose()
                                resetForm()
                            }}
                        />
                    </div>

                    <div className="modal-body">
                        {validationMessage?.message && (
                            <div className={`alert ${validationMessage.type === 'error' ? 'alert-danger'
                                : validationMessage.type === 'success' ? 'alert-success' : 'alert-warning'
                                }`}>
                                {validationMessage.message}
                            </div>
                        )}

                        {!showSummary ? (
                            <>
                                <div className="mb-3">
                                    <label className="form-label" htmlFor="promoCsv">
                                        Select CSV File
                                    </label>
                                    <input
                                        id="promoCsv"
                                        type="file"
                                        accept=".csv"
                                        className="form-control"
                                        onChange={handleFileChange}
                                        disabled={isUploading}
                                        ref={fileInputRef}
                                    />
                                    <div className="form-text">
                                        Required columns: {EXPECTED_CSV_FORMAT.join(', ')}
                                    </div>
                                </div>

                                <div className="alert alert-info">
                                    <h6>Example format:</h6>
                                    <pre className="mb-0">
                                        CODE,EXPIRATION_DATE<br />
                                        SAVE50,2025-12-31<br />
                                        NEWYEAR,2026-01-15
                                    </pre>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className={`alert ${summary.invalid ? 'alert-warning' : 'alert-success'}`}>
                                    <h5>Validation Summary</h5>
                                    <ul className="mb-0">
                                        <li>Total Rows: {summary.total}</li>
                                        <li>Valid: {summary.valid}</li>
                                        <li>Invalid: {summary.invalid}</li>
                                    </ul>
                                </div>

                                {summary.invalid > 0 && (
                                    <div className="table-responsive" style={{ maxHeight: 300 }}>
                                        <table className="table table-sm table-bordered">
                                            <thead className="sticky-top bg-light">
                                                <tr>
                                                    <th>Row</th>
                                                    <th>Code</th>
                                                    <th>Expiration</th>
                                                    <th>Issues</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {validationResults
                                                    .filter(r => r.errors.length)
                                                    .map((r, i) => (
                                                        <tr key={`err-${i}`}>
                                                            <td>{r.row}</td>
                                                            <td>{r.data.code}</td>
                                                            <td>{r.data.expiration_date}</td>
                                                            <td className="text-danger">{r.errors.join(', ')}</td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button
                            className="btn btn-secondary"
                            disabled={isUploading}
                            onClick={() => {
                                onClose()
                                resetForm()
                            }}
                        >
                            Cancel
                        </button>

                        {!showSummary ? (
                            <button
                                className="btn btn-primary"
                                disabled={!file || isUploading}
                                onClick={handleValidateCSV}
                            >
                                {isUploading ? 'Validating…' : 'Validate CSV'}
                            </button>
                        ) : (
                            <button
                                className="btn btn-success"
                                disabled={isUploading || !summary.valid}
                                onClick={handleUploadPromos}
                            >
                                {isUploading ? 'Uploading…' : `Upload ${summary.valid} Promos`}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PromoCSVUploadModal
